import { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import config from 'config';
import { BusinessUnit } from 'domains/brand.d';
import { FeatureFlag } from 'domains/feature-flag.d';
import { type IncentiveCampaignStarlinkType, type IncentiveCampaignType } from 'domains/loyalty.d';
import { useStarlinkContent, useFeatureFlag, useIncentiveCampaign } from 'hooks';
import { useGlobalContext } from 'store';

import { getCampaignStatusDetails } from '../helpers/get-campaign-details';

export const useLoyaltyIncentiveCampaign = () => {
  const { content: contentId } = useParams();

  const isEud = config.app.short === BusinessUnit.EUD;
  const {
    user: { isMultiBrand },
  } = useGlobalContext();

  const { optin, campaign } = useIncentiveCampaign;
  const {
    data: campaignData,
    isLoading: isLoadingCampaign,
    isError: isErrorCampaign,
  } = campaign.useQuery(contentId ?? '');

  const {
    data: starlinkData,
    isSuccess,
    isError: isErrorStarlinkContent,
    isLoading: isLoadingStarlinkContent,
    ...rest
  } = useStarlinkContent<IncentiveCampaignStarlinkType>({
    document: 'campanha_de_incentivo',
    slug: campaignData?.contentId ?? contentId,
  });

  const { data: optInData, isLoading: isLoadingOptin } = optin.useQuery(campaignData?.id);
  const starlinkContent: IncentiveCampaignType | undefined = useMemo(
    () => starlinkData?.items?.map((item) => item.data)[0],
    [starlinkData]
  );
  const { value: isLoyaltyCampaignEnabled, loading: isLoadingFeatureFlag } = useFeatureFlag(
    FeatureFlag.LOYALTY_INCENTIVE_CAMPAIGN,
    false
  );

  const campaignId = campaignData?.id ? campaignData.id : '';
  const isResaleIntention = optInData?.isResaleIntention ?? false;
  const isNotFound = optInData?.notFound ?? false;
  const { isCampaignActive, isResultActive, isOptinClosed } = getCampaignStatusDetails(
    campaignData?.status
  );
  const isError = isErrorCampaign || isErrorStarlinkContent;
  const isLoading =
    isLoadingStarlinkContent || isLoadingFeatureFlag || isLoadingOptin || isLoadingCampaign;
  const isEudAndVDF = isEud && isMultiBrand;
  const isContentAvailable = Object.keys(starlinkContent || {}).length > 0;
  const shouldShowCampaign = Boolean(
    !isEudAndVDF && isCampaignActive && isContentAvailable && isLoyaltyCampaignEnabled
  );

  return {
    data: starlinkContent,
    campaignId,
    isLoading,
    isError,
    isResaleIntention,
    isSuccess,
    shouldShowCampaign,
    isResultActive,
    isOptinClosed,
    isNotFound,
    optInData,
    contentId: campaignData?.contentId,
    ...rest,
  };
};
